import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Apple,
  Atom,
  Cherry,
  CircleDot,
  Eye,
  Flower,
  Flower2,
  Globe,
  Grape,
  HeartPulse,
  Link,
  Magnet,
  Microscope,
  RefreshCw,
  Scale,
  Shell,
  Sparkle,
  Sprout,
  Target,
  TreePalm,
  Brain,
  Clock,
  Dna,
  Droplet,
  FlaskConical,
  Heart,
  Leaf,
  Shield,
  Sparkles,
  Sun,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { EditorialIcon } from "@nh/shared";
import { SelectField } from "@/shared/ui/core";
import { useEditorialIconOptions } from "../../productOptions";

/**
 * Los doce iconos del contrato, resueltos por NOMBRE.
 *
 * `@nh/shared` lo comparte el servidor y el servidor no puede importar
 * de `lucide-react`, así que por el cable viaja `"bolt"` y la traducción
 * a un trazo ocurre en el frontend. La tienda tiene su gemelo en
 * `features/shop/components/ProductEditorialIcon`; los dos están
 * tipados como `Record<EditorialIcon, …>`, así que el día que el
 * contrato añada un icono y alguien olvide una de las dos tablas, el
 * error sale en `npm run typecheck` y no como un hueco en una ficha.
 */
const GLYPHS: Record<EditorialIcon, LucideIcon> = {
  bolt: Zap,
  dna: Dna,
  shield: Shield,
  leaf: Leaf,
  heart: Heart,
  brain: Brain,
  flask: FlaskConical,
  sparkles: Sparkles,
  clock: Clock,
  droplet: Droplet,
  sun: Sun,
  activity: Activity,
  atom: Atom,
  balance: Scale,
  berry: Cherry,
  cell: CircleDot,
  eye: Eye,
  flower: Flower2,
  globe: Globe,
  grape: Grape,
  leaves: Sprout,
  link: Link,
  magnet: Magnet,
  marigold: Flower,
  microscope: Microscope,
  palm: TreePalm,
  pomegranate: Apple,
  pulse: HeartPulse,
  refresh: RefreshCw,
  spark: Sparkle,
  spiral: Shell,
  target: Target,
};

export interface EditorialIconFieldProps {
  /** Ruta del campo: `"editorial.es.pillars.items.0.icon"`. */
  name: string;
  label?: string;
}

/**
 * Elegir el icono de un bloque editorial.
 *
 * ── Por qué un `<select>` y no una rejilla de trazos ─────────────────
 * Una rejilla de doce botones se ve mejor y es peor: hay que
 * reimplementar el teclado (flechas, inicio/fin), el rol de grupo de
 * radios y el nombre accesible de cada trazo, y quien no ve los iconos
 * se queda con doce botones sin nombre. El desplegable nativo trae todo
 * eso y en el móvil abre la rueda del sistema.
 *
 * Lo que sí hace falta es VER el icono elegido, porque "Matraz" y
 * "Destellos" no se distinguen hasta que están en la ficha: por eso al
 * lado del desplegable va la vista previa del trazo. Es decoración
 * —`aria-hidden`— porque el nombre ya lo dice el propio control.
 *
 * La lista es CERRADA, la del contrato: un icono inventado en la base no
 * puede tumbar la ficha porque el esquema lo rechaza antes.
 */
export function EditorialIconField({ name, label }: EditorialIconFieldProps) {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const options = useEditorialIconOptions();

  const value = watch(name) as string | undefined;
  const Glyph = value ? GLYPHS[value as EditorialIcon] : undefined;

  return (
    <div className="flex items-end gap-3">
      <SelectField
        name={name}
        label={label ?? t("Icono")}
        options={options}
        placeholder={t("Sin icono")}
        fieldClassName="flex-1"
      />
      <span
        aria-hidden="true"
        className="mb-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-ds-border bg-ds-cream-2 text-ds-ink"
      >
        {Glyph ? <Glyph className="h-5 w-5" strokeWidth={1.5} /> : null}
      </span>
    </div>
  );
}
